import platformClient, { type Models } from 'purecloud-platform-client-v2';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/messages';
import { BackendWebSocketService } from './BackendWebSocketService';
import config from '@/config';
import {
  MESSAGE_DIRECTION,
  PARTICIPANT_TYPE,
  PROCESSING_STAGE,
  GENESYS_CLOUD,
  type MessageDirection,
} from '@/constants';

interface GenesysWebSocketMessage {
  topicName?: string;
  eventBody?: Models.QueueConversationEventTopicConversation;
}

export const GenesysWebSocketService = {
  socket: null as WebSocket | null,
  reconnectAttempts: 0,
  // Track message IDs to prevent duplicate processing during history load and real-time events
  loadedMessageIds: new Set<string>(),

  /**
   * Extracts message IDs from conversation participants
   */
  _extractMessageIds(participants: Models.Participant[] | undefined): string[] {
    const messageIds: string[] = [];

    participants?.forEach((participant) => {
      if (
        participant.purpose === PARTICIPANT_TYPE.CUSTOMER ||
        participant.purpose === PARTICIPANT_TYPE.AGENT
      ) {
        participant.messages?.forEach((message) => {
          message.messages?.forEach((msg) => {
            if (msg.messageId) {
              messageIds.push(msg.messageId);
            }
          });
        });
      }
    });

    return messageIds;
  },

  /**
   * Extracts the human-readable text from a Genesys message.
   *
   * Web-messaging conversations leave the legacy `textBody` field empty and put
   * the content in `normalizedMessage` instead, so fall back through those
   * shapes before giving up.
   */
  _extractMessageText(msg: Models.MessageData): string {
    if (msg.textBody?.trim()) {
      return msg.textBody;
    }

    const normalized = msg.normalizedMessage;
    if (normalized?.text?.trim()) {
      return normalized.text;
    }

    const textFromContent = normalized?.content
      ?.map((part) => part.text?.body)
      .filter((body): body is string => !!body?.trim())
      .join('\n');

    return textFromContent || '';
  },

  /**
   * Loads conversation history (existing messages) when the app first opens
   * This runs BEFORE establishing WebSocket connection to populate initial messages
   */
  async loadConversationHistory(conversationId: string) {
    const messageStore = useMessageStore();

    if (!conversationId || typeof conversationId !== 'string') {
      console.error('Invalid conversation ID provided');
      return;
    }

    try {
      const conversationsApi = new platformClient.ConversationsApi();

      // Clear existing messages and reset tracking to start fresh
      messageStore.clearMessages();
      this.loadedMessageIds.clear();

      // Step 1: Get conversation metadata which contains message references (not full messages)
      const conversation =
        await conversationsApi.getConversation(conversationId);

      // Step 2: Extract message IDs from nested participant structure
      const messageIds = this._extractMessageIds(conversation.participants);

      if (messageIds.length === 0) {
        console.warn(
          'No message IDs found - this might be normal if no messages exist yet'
        );
        return;
      }

      // Step 3: Fetch all message details in one bulk API call.
      // `useNormalizedMessage` is required: Genesys deprecated the legacy
      // `textBody`/`media`/`stickers` fields (removal 2026-03-31); message
      // content is only guaranteed in `normalizedMessage`.
      const messagesResponse =
        await conversationsApi.postConversationsMessageMessagesBulk(
          conversationId,
          { body: messageIds, useNormalizedMessage: true }
        );

      // Step 4: Process and store messages in chronological order
      if (messagesResponse.entities) {
        // Reverse for proper chronological order (oldest to newest)
        const messages = [...messagesResponse.entities].reverse();

        messages.forEach((msg) => {
          const text = this._extractMessageText(msg);
          // Only process messages with actual text content
          if (text && msg.id) {
            // Track loaded messages to prevent duplicates when WebSocket events come in
            this.loadedMessageIds.add(msg.id);

            messageStore.addMessage({
              id: msg.id,
              text,
              direction:
                msg.direction === MESSAGE_DIRECTION.INBOUND
                  ? MESSAGE_DIRECTION.INBOUND
                  : MESSAGE_DIRECTION.OUTBOUND,
              fromMe: msg.direction === MESSAGE_DIRECTION.OUTBOUND,
              timestamp: msg.timestamp || new Date().toISOString(),
            });
          } else {
            console.warn('Skipping message - no text or ID:', msg);
          }
        });
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  },

  /**
   * Connects to Genesys Cloud WebSocket to receive conversation events
   */
  async connect() {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();
    if (
      !authStore.isLoggedIn ||
      !authStore.conversationId ||
      !authStore.userId
    ) {
      console.error(
        'Cannot connect to Genesys WebSocket: not logged in, missing conversation ID, or no user ID'
      );
      return;
    }

    try {
      const userId = authStore.userId;

      // Step 1: Create notification channel - this gives us a WebSocket URI
      const notificationsApi = new platformClient.NotificationsApi();
      const channel = await notificationsApi.postNotificationsChannels();

      if (!channel.connectUri) {
        throw new Error('No WebSocket URI received from Genesys Cloud');
      }

      // Step 2: Subscribe to conversation events for this user
      // This topic will send events for ALL conversations the user is part of
      const topics = [
        {
          id: `v2.users.${userId}.conversations`,
        },
      ];

      // Subscribe to the topics
      await notificationsApi.putNotificationsChannelSubscriptions(
        channel.id!,
        topics
      );

      // Step 3: Establish WebSocket connection
      this.socket = new WebSocket(channel.connectUri);

      // Connection opened successfully
      this.socket.onopen = () => {
        messageStore.setConnectionStatus(true);
        this.reconnectAttempts = 0; // Reset reconnection counter
      };

      // Handle incoming messages (real-time events)
      this.socket.onmessage = (event) => {
        this.handleMessage(JSON.parse(event.data));
      };

      // Handle connection errors
      this.socket.onerror = (error) => {
        console.error('Genesys WebSocket error:', error);
        messageStore.setConnectionStatus(false);
      };

      // Handle connection closing (both expected and unexpected)
      this.socket.onclose = () => {
        messageStore.setConnectionStatus(false);
        this.handleReconnection(); // Attempt to reconnect automatically
      };
    } catch (error) {
      console.error('Failed to connect to Genesys WebSocket:', error);
      messageStore.setConnectionStatus(false);
    }
  },

  /**
   * Checks if a WebSocket message should be processed
   */
  _shouldProcessMessage(
    data: GenesysWebSocketMessage,
    conversationId: string
  ): boolean {
    // Ignore heartbeat/metadata messages from Genesys
    if (data.topicName?.toLowerCase() === GENESYS_CLOUD.CHANNEL_METADATA) {
      return false;
    }

    // Filter events - only process events for our current conversation
    if (data.eventBody?.id !== conversationId) {
      return false;
    }

    // Only process conversation message events
    return !!(data.topicName?.includes('.conversations') && data.eventBody);
  },

  /**
   * Processes participants to find new messages
   */
  _processParticipants(
    participants: Models.QueueConversationEventTopicParticipant[],
    conversationId: string
  ) {
    const messageStore = useMessageStore();

    participants.forEach((participant) => {
      if (
        !participant.endTime &&
        participant.messages &&
        participant.messages.length > 0
      ) {
        // Get the latest message group from this participant
        const latestMessageGroup =
          participant.messages[participant.messages.length - 1];

        if (
          latestMessageGroup.messages &&
          latestMessageGroup.messages.length > 0
        ) {
          // Get the most recent message from the group
          const latestMessage =
            latestMessageGroup.messages[latestMessageGroup.messages.length - 1];

          // Check if this is a new message we haven't processed yet
          const existingMessage = messageStore.messages.find(
            (m) => m.id === latestMessage.messageId
          );
          if (
            !existingMessage &&
            latestMessage.messageId &&
            !this.loadedMessageIds.has(latestMessage.messageId)
          ) {
            // Fetch full message details (WebSocket only gives us references)
            this.fetchMessageDetails(
              conversationId,
              latestMessage.messageId,
              participant.purpose || MESSAGE_DIRECTION.UNKNOWN
            );
          }
        }
      }
    });
  },

  /**
   * Determines whether the conversation has ended.
   *
   * Genesys does not send an explicit "chat closed" event on the conversation
   * topic; instead each participant gets an `endTime` when it disconnects. The
   * chat is considered ended only once every customer and agent participant has
   * an `endTime` — i.e. both the customer has left *and* the CS agent has ended
   * the conversation. A participant still missing an `endTime` means someone is
   * still active.
   */
  _isConversationEnded(
    participants: Models.QueueConversationEventTopicParticipant[] | undefined
  ): boolean {
    const relevant = (participants ?? []).filter(
      (p) =>
        p.purpose === PARTICIPANT_TYPE.CUSTOMER ||
        p.purpose === PARTICIPANT_TYPE.AGENT
    );

    return relevant.length > 0 && relevant.every((p) => !!p.endTime);
  },

  /**
   * Handles incoming messages from Genesys WebSocket
   */
  handleMessage(data: GenesysWebSocketMessage) {
    const authStore = useAuthStore();

    if (!this._shouldProcessMessage(data, authStore.conversationId!)) {
      return;
    }

    // Handle conversation message events
    const conversationData = data.eventBody!;

    // Process each active participant in the conversation
    if (conversationData.participants) {
      this._processParticipants(
        conversationData.participants,
        authStore.conversationId!
      );

      // Detect whether the conversation has fully ended (customer left and the
      // CS agent has wrapped up). On the transition to ended, request a summary.
      const messageStore = useMessageStore();
      const ended = this._isConversationEnded(conversationData.participants);
      if (ended && !messageStore.conversationEnded) {
        console.info('Conversation ended (all participants disconnected)');
        void BackendWebSocketService.sendSummaryRequest();
      }
      messageStore.setConversationEnded(ended);
    }
  },

  /**
   * Fetches full message details from Genesys API
   * WebSocket events only contain message references, not actual content
   */
  async fetchMessageDetails(
    conversationId: string,
    messageId: string,
    participantPurpose: string
  ) {
    if (!conversationId || !messageId) {
      console.error('Invalid conversation ID or message ID provided');
      return;
    }

    const messageStore = useMessageStore();

    try {
      const conversationsApi = new platformClient.ConversationsApi();
      const messageDetail =
        await conversationsApi.getConversationsMessageMessage(
          conversationId,
          messageId,
          { useNormalizedMessage: true }
        );

      // Only process messages with text content
      const text = this._extractMessageText(messageDetail);
      if (text) {
        // Track this message as loaded to prevent duplicate processing
        this.loadedMessageIds.add(messageId);

        const newMessage = {
          id: messageId,
          text,
          direction: (messageDetail.direction === MESSAGE_DIRECTION.INBOUND
            ? MESSAGE_DIRECTION.INBOUND
            : MESSAGE_DIRECTION.OUTBOUND) as MessageDirection,
          fromMe: participantPurpose === PARTICIPANT_TYPE.AGENT,
          timestamp: new Date().toISOString(),
        };

        messageStore.addMessage(newMessage);

        // Trigger backend processing based on message type and sequence
        this.handleMessageForBackend(newMessage);
      }
    } catch (error) {
      console.error('Failed to fetch message details:', error);
    }
  },

  /**
   * Handle message processing for backend - considering agent-customer pairs
   */
  async handleMessageForBackend(message: {
    id: string;
    text: string;
    direction: MessageDirection;
    fromMe: boolean;
    timestamp: string;
  }) {
    if (!BackendWebSocketService.isConnected) {
      console.warn('Backend not connected, skipping message processing');
      return;
    }
    const messageStore = useMessageStore();

    if (message.direction === MESSAGE_DIRECTION.INBOUND) {
      // Collect ALL unsent agent messages that should be sent as context
      const allMessages = messageStore.messages;
      const currentIndex = allMessages.findIndex((m) => m.id === message.id);
      const agentMessagesToSend: (typeof message)[] = [];

      if (currentIndex > 0) {
        // Look for ALL consecutive agent messages before this customer message
        for (let i = currentIndex - 1; i >= 0; i--) {
          const previousMessage = allMessages[i];

          if (
            previousMessage.direction === MESSAGE_DIRECTION.OUTBOUND &&
            !BackendWebSocketService.sentMessageIds.has(previousMessage.id)
          ) {
            // Add to the beginning of array to maintain chronological order
            agentMessagesToSend.unshift({
              id: previousMessage.id,
              text: previousMessage.text,
              direction: previousMessage.direction as MessageDirection,
              fromMe: previousMessage.fromMe,
              timestamp: previousMessage.timestamp,
            });
          } else if (previousMessage.direction === MESSAGE_DIRECTION.INBOUND) {
            // Stop looking when we hit a customer message (conversation boundary)
            break;
          }
        }
      }

      // Send agent messages first in chronological order
      for (const agentMessage of agentMessagesToSend) {
        await this.sendMessageToBackend(agentMessage);
      }

      // Then send the customer message
      await this.sendMessageToBackend(message);
    } else if (message.direction === MESSAGE_DIRECTION.OUTBOUND) {
      // Agent message - send directly to backend
      await this.sendMessageToBackend(message);
    }
  },

  /**
   * Send message to backend service
   */
  async sendMessageToBackend(message: {
    id: string;
    text: string;
    direction: MessageDirection;
    fromMe: boolean;
    timestamp: string;
  }) {
    const messageStore = useMessageStore();

    if (BackendWebSocketService.isConnected) {
      // Only show loading state for customer messages that trigger AI responses
      // Agent messages are sent as context and don't need loading indicators
      if (message.direction === MESSAGE_DIRECTION.INBOUND) {
        messageStore.setAiResponseProcessingStatus(
          true,
          PROCESSING_STAGE.SENDING
        );
      }

      try {
        await BackendWebSocketService.sendMessageToBackend(message);
        if (message.direction === MESSAGE_DIRECTION.INBOUND) {
          messageStore.setAiResponseProcessingStatus(
            true,
            PROCESSING_STAGE.GENERATING
          );
        }
      } catch (error) {
        console.error('Failed to send message to backend:', error);
        // Only show error state if this was a customer message
        if (message.direction === MESSAGE_DIRECTION.INBOUND) {
          messageStore.setAiResponseProcessingStatus(
            false,
            PROCESSING_STAGE.ERROR
          );
        }
      }
    }
  },

  /**
   * Send a message to the customer via Genesys Cloud
   */
  async sendMessageToCustomer(messageText: string): Promise<boolean> {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();

    try {
      const conversationsApi = new platformClient.ConversationsApi();

      // Get current conversation to find agent participant
      const conversation = await conversationsApi.getConversation(
        authStore.conversationId!
      );

      // Get the last agent participant - this pattern is from the official Genesys blueprint
      // and fixes an issue when an agent gets reconnected and reassigned a new participant ID.
      const agentsArr = conversation.participants?.filter(
        (p) => p.purpose === PARTICIPANT_TYPE.AGENT
      );

      if (!agentsArr || agentsArr.length === 0) {
        console.error('No agent participants found in conversation');
        return false;
      }

      const agent = agentsArr[agentsArr.length - 1];

      if (!agent || agent.endTime) {
        console.error('No active agent participant found');
        return false;
      }

      // Use the first message ID as communication ID - this is the established pattern
      // in Genesys messaging. Each participant has a messages array where the first
      // message contains the communication channel ID used for sending messages.
      const communicationId = agent.messages?.[0]?.id;

      if (!communicationId) {
        console.error('No communication ID found for agent participant');
        return false;
      }

      // Send the message
      await conversationsApi.postConversationsMessageCommunicationMessages(
        authStore.conversationId!,
        communicationId,
        {
          textBody: messageText,
        }
      );

      // Call similarity service for monitoring (compare AI-generated vs agent-sent message)
      // This helps track how often agents modify AI responses before sending
      if (
        BackendWebSocketService.isConnected &&
        messageStore.aiResponses.length > 0
      ) {
        const latestAiResponse = messageStore.aiResponses[0];
        const aiGeneratedText = latestAiResponse.translation;

        if (aiGeneratedText && aiGeneratedText.trim()) {
          // Send similarity request to compare AI-generated text with agent-sent text
          BackendWebSocketService.sendSimilarityRequest(
            aiGeneratedText,
            messageText
          );
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to send message to customer:', error);
      return false;
    }
  },

  /**
   * Handles automatic reconnection with fixed delay
   */
  handleReconnection() {
    if (this.reconnectAttempts < config.webSocket.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => this.connect(), config.webSocket.reconnectDelay);
    }
  },

  /**
   * Disconnects from Genesys WebSocket
   */
  disconnect() {
    const messageStore = useMessageStore();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    messageStore.setConnectionStatus(false);
  },
};
